import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import DataTable from "../DataTable/DataTable";
import { PiFileCsv } from "react-icons/pi";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sow-tabpanel-${index}`}
      aria-labelledby={`sow-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SOWDetailsModal({ open, handleClose, sowData }) {
  const [tabValue, setTabValue] = useState(0);
// console.log("sowData",sowData)
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

    React.useEffect(() => {
    if (open) {
      setTabValue(0);
    }
  }, [open]);

    // CSV Download Helper Function
  const downloadCSV = (columns: any[], rows: any[], filename: string) => {
    // Create CSV header
    const headers = columns.map((col) => col.headerName).join(",");

    // Create CSV rows
    const csvRows = rows.map((row) =>
      columns.map((col) => {
        const value = row[col.field];
        // Escape commas and quotes in values
        const escaped = String(value).replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(",")
    );

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!sowData) return null;

  // Setup Charges Columns & Rows
  const setupChargesColumns = [
    { field: "code", headerName: "Code", width: 180 },
    { field: "description", headerName: "Description", width: 250 },
    // { field: "country", headerName: "Country", width: 130 },
    // { field: "did_type", headerName: "DID Type", width: 130 },
    { field: "quantity", headerName: "Quantity", width: 110 },
    { field: "amount", headerName: "Amount", width: 110 },
    { field: "total_amount", headerName: "Total", width: 110 },
  ];

  const setupChargesRows = sowData?.setup_charges
  ?.filter((item)=>   item?.description &&
        item.description.trim() !== "" &&
        item.description.toLowerCase() !== "n/a")
        ?.map((item, index) => ({
    id: index,
    code: item?.code || "N/A",
    description: item?.description || "N/A",
    // country: item?.country || "N/A",
    // did_type: item?.did_type || "N/A",
    quantity: item?.quantity ?? 0,
    amount: item?.amount ?? 0,
    total_amount: item?.total_amount ?? 0,
  })) || [];

  // Monthly Charges Columns & Rows
  const monthlyChargesColumns = [
    { field: "code", headerName: "Code", width: 200 },
    { field: "description", headerName: "Description", width: 280 },
    // { field: "country", headerName: "Country", width: 130 },
    // { field: "did_type", headerName: "DID Type", width: 130 },
    { field: "type", headerName: "Type", width: 180 },
    { field: "quantity", headerName: "Quantity", width: 110 },
    { field: "unit_price", headerName: "Unit Price", width: 120 },
    { field: "total_price", headerName: "Total Price", width: 120 },
  ];

  const monthlyChargesRows = sowData?.monthly_charges?.filter(
      (item) =>
        item?.description &&
        item.description.trim() !== "" &&
        item.description.toLowerCase() !== "n/a"
    )
    ?.map((item, index) => ({
    id: index,
    code: item?.code || "N/A",
    description: item?.description || "N/A",
    // country: item?.country || "N/A",
    // did_type: item?.did_type || "N/A",
    type: item?.type || "N/A",
    quantity: item?.quantity ?? 0,
    unit_price: item?.unit_price ?? 0,
    total_price: item?.total_price ?? 0,
  })) || [];

  // Call Rates Columns & Rows
  const callRatesColumns = [
    { field: "country", headerName: "Country", width: 200 },
    { field: "landline_rate", headerName: "Landline Rate", width: 150 },
    { field: "mobile_rate", headerName: "Mobile Rate", width: 150 },
    { field: "call_group", headerName: "Call Group", width: 250 },
  ];

  const callRatesRows = sowData?.call_rates?.map((item, index) => ({
    id: index,
    country: item?.country || "N/A",
    landline_rate: item?.landline_rate ?? 0,
    mobile_rate: item?.mobile_rate ?? 0,
    call_group: item?.call_group || "N/A",
  })) || [];

  // Premium Call Rates Columns & Rows
  const premiumCallRatesColumns = [
    { field: "country", headerName: "Country", width: 250 },
    { field: "rate", headerName: "Rate", width: 150 },
    { field: "call_group", headerName: "Call Group", width: 350 },
  ];

  const premiumCallRatesRows = sowData?.premium_call_rates?.map((item, index) => ({
    id: index,
    country: item?.country || "N/A",
    rate: item?.rate ?? 0,
    call_group: item?.call_group || "N/A",
  })) || [];

  // Ancillary Charges Columns & Rows
  const ancillaryChargesColumns = [
    { field: "code", headerName: "Code", width: 180 },
    { field: "description", headerName: "Description", width: 300 },
    { field: "per_unit", headerName: "Per Unit", width: 200 },
    { field: "amount", headerName: "Amount", width: 130 },
  ];

  const ancillaryChargesRows = sowData?.ancillary_charges?.map((item, index) => ({
    id: index,
    code: item?.code || "N/A",
    description: item?.description || "N/A",
    per_unit: item?.per_unit || "N/A",
    amount: item?.amount ?? 0,
  })) || [];

  // Inbound Per Minute Rates Columns & Rows
  const inboundRatesColumns = [
    { field: "country", headerName: "Country", width: 180 },
    { field: "toll", headerName: "Toll", width: 130 },
    { field: "toll_free", headerName: "Toll Free", width: 130 },
    { field: "tf_mobile", headerName: "TF Mobile", width: 130 },
  ];

  const inboundRatesRows = sowData?.inbound_per_minute_rates?.map((item, index) => ({
    id: index,
    country: item?.country || "N/A",
    toll: item?.toll ?? 0,
    toll_free: item?.toll_free ?? 0,
    tf_mobile: item?.tf_mobile ?? 0,
  })) || [];

  return (
    <Modal
      open={open}
      onClose={(e, reason) => {
        if (reason !== "backdropClick") {
          handleClose(e);
        }
      }}
      style={{ zIndex: 99 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          width: "95%",
          maxWidth: "1200px",
          maxHeight: "80vh",
          height: "80vh",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h5" component="h2">
            SOW Details - {sowData?.sow_id || "N/A"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* CSV Download Button - Show only on table tabs */}
            {tabValue >= 1 && tabValue <= 6 && (
              <Tooltip title="Download CSV" placement="top" arrow>
                <span
                  onClick={() => {
                    const csvMap = {
                      1: { cols: setupChargesColumns, rows: setupChargesRows, name: "Setup_Charges" },
                      2: { cols: monthlyChargesColumns, rows: monthlyChargesRows, name: "Monthly_Charges" },
                      3: { cols: callRatesColumns, rows: callRatesRows, name: "Outgoing_Call_Rates" },
                      4: { cols: premiumCallRatesColumns, rows: premiumCallRatesRows, name: "Premium_Call_Rates" },
                      5: { cols: ancillaryChargesColumns, rows: ancillaryChargesRows, name: "Ancillary_Charges" },
                      6: { cols: inboundRatesColumns, rows: inboundRatesRows, name: "Inbound_Per_Minute_Rates" },
                    };
                    const data = csvMap[tabValue];
                    if (data) {
                      downloadCSV(data.cols, data.rows, `${sowData?.sow_id || "SOW"}_${data.name}`);
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#f5f5f5",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  <PiFileCsv size={20} color="#333" />
                </span>
              </Tooltip>
            )}
            <IoClose
              size={28}
              onClick={handleClose}
              style={{ cursor: "pointer" }}
            />
          </Box>
        </Box>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" />
            <Tab label="Setup Charges" />
            <Tab label="Monthly Charges" />
            <Tab label="Outgoing Call Rates" />
            <Tab label="Premium Call Rates" />
            <Tab label="Ancillary Charges" />
            <Tab label="Inbound/Minute Rates" />
            <Tab label="Signatures" />
          </Tabs>
        </Box>

        {/* Tab Content - Scrollable */}
        <Box sx={{ overflowY: "auto", px: 3, flex: 1 }}>
          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              {/* Client Information */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Client Information
                    </Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong> {sowData?.client?.name || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Address:</strong> {sowData?.client?.address || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>City:</strong> {sowData?.client?.city || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Postcode:</strong> {sowData?.client?.postcode || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong> {sowData?.client?.contact_email || "N/A"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Contract Information */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Contract Information
                    </Typography>
                    <Typography variant="body2">
                      <strong>Duration:</strong> {sowData?.sow_details?.duration_months || 0} months
                    </Typography>
                    <Typography variant="body2">
                      <strong>Currency:</strong> {sowData?.sow_details?.currency || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Quote Expiry:</strong> {sowData?.sow_details?.quote_expiry_date || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Technology:</strong> {sowData?.sow_details?.contract_info?.technology || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>City:</strong> {sowData?.sow_details?.contract_city || "N/A"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Summary Charges */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Summary Charges
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Monthly Voice Charges
                        </Typography>
                        <Typography variant="h6">
                          {sowData?.sow_details?.summary_charges?.monthly_voice_charges ?? 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Annual Charges
                        </Typography>
                        <Typography variant="h6">
                          {sowData?.sow_details?.summary_charges?.annual_charges ?? 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Verified Annual Charges
                        </Typography>
                        <Typography variant="h6">
                          {Number(sowData?.verified_annual_charges ?? 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          One-Time Charges
                        </Typography>
                        <Typography variant="h6">
                          {sowData?.sow_details?.summary_charges?.one_time_voice_charges ?? 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          36-Month Contract Value
                        </Typography>
                        <Typography variant="h6">
                          {sowData?.sow_details?.summary_charges?.contract_36_month_value ?? 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Verified 36-Month Contract Value
                        </Typography>
                        <Typography variant="h6">
                          {Number(sowData?.verified_contract_36_month_value ?? 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Setup Charges Tab */}
          <TabPanel value={tabValue} index={1}>
            <DataTable columns={setupChargesColumns} rows={setupChargesRows} />
          </TabPanel>

          {/* Monthly Charges Tab */}
          <TabPanel value={tabValue} index={2}>
            <DataTable columns={monthlyChargesColumns} rows={monthlyChargesRows} />
          </TabPanel>

          {/* Call Rates Tab */}
          <TabPanel value={tabValue} index={3}>
            <DataTable columns={callRatesColumns} rows={callRatesRows} />
          </TabPanel>

          {/* Premium Call Rates Tab */}
          <TabPanel value={tabValue} index={4}>
            <DataTable columns={premiumCallRatesColumns} rows={premiumCallRatesRows} />
          </TabPanel>

          {/* Ancillary Charges Tab */}
          <TabPanel value={tabValue} index={5}>
            <DataTable columns={ancillaryChargesColumns} rows={ancillaryChargesRows} />
          </TabPanel>

          {/* Inbound Rates Tab */}
          <TabPanel value={tabValue} index={6}>
            <DataTable columns={inboundRatesColumns} rows={inboundRatesRows} />
          </TabPanel>

          {/* Signatures Tab */}
<TabPanel value={tabValue} index={7}>
  <Grid container spacing={3}>
    {/* Supplier Signatures */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Supplier Signatures
          </Typography>
          {sowData?.signatures
            ?.filter((sig) => sig?.party === "supplier")
            ?.map((sig, index) => (
              <Box key={sig?.id || index} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Name:</strong> {sig?.name || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Position:</strong> {sig?.position || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {sig?.date || "N/A"}
                </Typography>
              </Box>
            ))}
        </CardContent>
      </Card>
    </Grid>

    {/* Client Signatures */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Client Signatures
          </Typography>
          {sowData?.signatures
            ?.filter((sig) => sig?.party === "client")
            ?.map((sig, index) => (
              <Box key={sig?.id || index} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Name:</strong> {sig?.name || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Position:</strong> {sig?.position || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {sig?.date || "N/A"}
                </Typography>
              </Box>
            ))}
        </CardContent>
      </Card>
    </Grid>
  </Grid>
</TabPanel>
        </Box>
      </Box>
    </Modal>
  );
}